/**
 * client/src/components/session-possession-prompt.tsx — the explanation that
 * precedes the possession heartbeat's passkey sheet (ledger #119 residual).
 *
 * WHY THIS EXISTS. The heartbeat is triggered by a READ, not by anything the
 * holder did. Shipped without this, its first appearance is a browser passkey
 * sheet opening in the middle of opening the inbox, with nothing on screen
 * saying why — the exact shape of a prompt people learn to dismiss, or worse,
 * learn to tap through without reading. That habit is what every
 * passkey-adjacent lure depends on, so an unexplained prompt is not merely poor
 * comprehension here; it trains the behaviour the ceremony exists to make
 * expensive. First-Use Rule (DESIGN-SYSTEM.md §7.3 rule 1): the step names what
 * it buys.
 *
 * WHAT IT MAY AND MAY NOT SAY (CLAUDE.md §Friction Rule clause 2). The copy
 * claims possession and freshness — that the passkey is in hand, now — and
 * NOTHING else. No liveness, no "we know it's a real person", no deepfake
 * language. "Confirm it's still you" is a claim about the authenticator, and
 * the body says so in as many words.
 *
 * ONE EXTRA TAP, AND WHAT IT BUYS. Before this, the flow was: sheet appears.
 * Now it is: dialog, tap, sheet appears. That added tap pays for itself twice —
 * the holder can tell a legitimate mid-read prompt from a spurious one, and
 * `navigator.credentials.get()` is driven by a real user gesture instead of a
 * background timer, which browsers treat more predictably. The second is a
 * secondary benefit and is deliberately not the argument: the comprehension one
 * is.
 *
 * Colour: `interactive` blue for the primary action (DESIGN-SYSTEM.md §3.2) —
 * NOT emerald, which CLAUDE.md reserves for success / hardware-confirmed / AAL
 * milestones, and this ceremony confirms nothing new and promotes nothing; NOT
 * amber, which is for user errors, until the ceremony actually fails.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { KeyRound } from "lucide-react";

import { AdaptiveDialog } from "@/components/ui/adaptive-dialog";
import { Button } from "@/components/ui/button";
import {
  registerPossessionConfirmation,
  POSSESSION_PROOF_EVENT,
  type PossessionProofPhase,
} from "@/lib/session-possession";

/** What the holder chose, or how far the ceremony behind it got. */
type PromptPhase = "idle" | "asking" | "checking" | "failed";

interface PendingConfirmation {
  resolve: () => void;
  reject: (reason: Error) => void;
}

export const POSSESSION_PROMPT_DISMISSED =
  "Confirmation was not completed, so the page was not opened.";

export function SessionPossessionPrompt(): React.ReactElement | null {
  const [phase, setPhase] = useState<PromptPhase>("idle");
  const pendingRef = useRef<PendingConfirmation | null>(null);

  // Settle whatever is pending exactly once. Both paths clear the slot first,
  // so a double-click on either button cannot resolve and then reject the same
  // promise (which would leave `runCeremony` continuing past a "Not now").
  const settle = useCallback((outcome: "confirm" | "dismiss") => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    if (outcome === "confirm") {
      setPhase("checking");
      pending.resolve();
    } else {
      setPhase("idle");
      pending.reject(new Error(POSSESSION_PROMPT_DISMISSED));
    }
  }, []);

  useEffect(() => {
    const unregister = registerPossessionConfirmation(
      () =>
        new Promise<void>((resolve, reject) => {
          // Single-flight lives in session-possession.ts, so a second
          // confirmation cannot be requested while one is open. Refusing here
          // as well keeps that a property of THIS component too, rather than
          // something it inherits and would silently lose if the ceremony's
          // dedupe ever changed.
          if (pendingRef.current) {
            reject(new Error(POSSESSION_PROMPT_DISMISSED));
            return;
          }
          pendingRef.current = { resolve, reject };
          setPhase("asking");
        }),
    );
    return () => {
      unregister();
      // Settle anything still open. Without this, unmounting mid-prompt leaves
      // `runCeremony` awaiting a promise with no remaining path to settle it,
      // and the gated read that triggered it never resolves OR rejects — a
      // silent permanent spinner. This component sits at the app shell and does
      // not unmount in normal use, which is exactly why the failure would be
      // rare, undiagnosable, and worth closing structurally.
      const pending = pendingRef.current;
      pendingRef.current = null;
      pending?.reject(new Error(POSSESSION_PROMPT_DISMISSED));
    };
  }, []);

  // The ceremony's own progress, after the holder has said yes. `started` is
  // emitted only once confirmation resolved, so it can never race the dialog
  // open (session-possession.ts orders the two deliberately).
  useEffect(() => {
    const onProgress = (event: Event): void => {
      const detail = (event as CustomEvent<{ phase?: PossessionProofPhase }>).detail;
      if (detail?.phase === "succeeded") setPhase("idle");
      if (detail?.phase === "failed") setPhase("failed");
    };
    window.addEventListener(POSSESSION_PROOF_EVENT, onProgress);
    return () => window.removeEventListener(POSSESSION_PROOF_EVENT, onProgress);
  }, []);

  const open = phase === "asking" || phase === "checking" || phase === "failed";
  if (!open) return null;

  const failed = phase === "failed";

  return (
    <AdaptiveDialog
      open
      // Dismissal is only offered while we are ASKING. Once the passkey sheet
      // is up, the browser owns the interaction and closing this behind it
      // would strand `runCeremony` awaiting a promise nobody can settle.
      onOpenChange={(next) => {
        if (!next && phase === "asking") settle("dismiss");
        if (!next && failed) setPhase("idle");
      }}
      title={failed ? "That check didn't finish" : "Confirm it's still you"}
    >
      <div className="space-y-4">
        <div className="flex gap-3">
          <div
            className={`shrink-0 rounded-xl p-2 ${
              failed ? "bg-warning/10" : "bg-interactive/10"
            }`}
          >
            <KeyRound
              className={`h-5 w-5 ${failed ? "text-warning" : "text-interactive"}`}
              aria-hidden="true"
            />
          </div>
          <div className="space-y-2 text-sm leading-relaxed text-text-secondary">
            {failed ? (
              <p>
                Nothing was opened. You can try again, or carry on elsewhere and
                confirm when you next need this page.
              </p>
            ) : (
              <>
                <p>
                  Your account is kept locked to the passkey you signed in with.
                  We ask for it again so that a copy of your browser session,
                  taken from this device, cannot use it somewhere else.
                </p>
                <p className="text-text-tertiary">
                  This checks that your passkey is in your hands right now. It is
                  not a check of who is holding it.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="primary"
            className="flex-1"
            loading={phase === "checking"}
            disabled={phase === "checking"}
            onClick={() => (failed ? setPhase("idle") : settle("confirm"))}
          >
            {failed ? "Close" : "Confirm it's you"}
          </Button>
          {phase === "asking" && (
            <Button variant="secondary" className="flex-1" onClick={() => settle("dismiss")}>
              Not now
            </Button>
          )}
        </div>
      </div>
    </AdaptiveDialog>
  );
}
